import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { FileText, Settings2, AlertTriangle, CheckCircle2, BookOpen, MessageSquareWarning, ChevronDown, Sparkles, Save, Download, HelpCircle } from 'lucide-react';
export function DraftingCopilot() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(true); // Default to true for demo purposes
  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setHasGenerated(true);
    }, 1500);
  };
  return <div className="space-y-6 h-[calc(100vh-8rem)] flex flex-col" data-id="element-935">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0" data-id="element-936">
        <div data-id="element-937">
          <div className="flex items-center space-x-2" data-id="element-938">
            <h1 className="text-2xl font-bold tracking-tight" data-id="element-939">
              Drafting Copilot
            </h1>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-id="element-940">

              Beta
            </Badge>
          </div>
          <p className="text-muted-foreground" data-id="element-941">
            AI-assisted first-draft generation grounded in your local matter
            corpus.
          </p>
        </div>
        <div className="flex items-center space-x-2 bg-amber-50 text-amber-800 px-4 py-2 rounded-md border border-amber-200 text-sm font-medium" data-id="element-942">
          <AlertTriangle className="w-4 h-4 mr-2 text-amber-600" data-id="element-943" />
          Attorney review required before use
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 flex-1 min-h-0" data-id="element-944">
        {/* LEFT PANEL: Drafting Controls */}
        <Card className="lg:col-span-3 flex flex-col h-full overflow-hidden border-slate-200 shadow-sm" data-id="element-945">
          <CardHeader className="py-4 border-b bg-slate-50/50 shrink-0" data-id="element-946">
            <CardTitle className="text-sm flex items-center" data-id="element-947">
              <Settings2 className="w-4 h-4 mr-2 text-slate-500" data-id="element-948" />
              Drafting Parameters
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 overflow-y-auto flex-1 space-y-5" data-id="element-949">
            <div className="space-y-2" data-id="element-950">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider" data-id="element-951">
                Matter
              </label>
              <div className="relative" data-id="element-952">
                <select className="w-full h-9 px-3 py-1 text-sm rounded-md border border-input bg-transparent shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring appearance-none" data-id="element-953">
                  <option data-id="element-954">Ramirez v. Lone Star Freight</option>
                  <option data-id="element-955">Johnson Auto Collision Case</option>
                  <option data-id="element-956">Medina Medical Negligence</option>
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-957" />
              </div>
            </div>

            <div className="space-y-2" data-id="element-958">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider" data-id="element-959">
                Draft Type
              </label>
              <div className="relative" data-id="element-960">
                <select className="w-full h-9 px-3 py-1 text-sm rounded-md border border-input bg-transparent shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring appearance-none" data-id="element-961">
                  <option data-id="element-962">Demand Letter</option>
                  <option data-id="element-963">Petition</option>
                  <option data-id="element-964">Motion for Summary Judgment</option>
                  <option data-id="element-965">Discovery Requests</option>
                  <option data-id="element-966">Discovery Responses</option>
                  <option data-id="element-967">Case Summary</option>
                  <option data-id="element-968">Deposition Outline</option>
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-969" />
              </div>
            </div>

            <div className="space-y-2" data-id="element-970">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider" data-id="element-971">
                Source Materials
              </label>
              <div className="space-y-2 border rounded-md p-3 bg-slate-50/50" data-id="element-972">
                <label className="flex items-center space-x-2 text-sm" data-id="element-973">
                  <input type="checkbox" defaultChecked className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-974" />

                  <span data-id="element-975">All Medical Records (12)</span>
                </label>
                <label className="flex items-center space-x-2 text-sm" data-id="element-976">
                  <input type="checkbox" defaultChecked className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-977" />

                  <span data-id="element-978">Police Report (1)</span>
                </label>
                <label className="flex items-center space-x-2 text-sm" data-id="element-979">
                  <input type="checkbox" defaultChecked className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-980" />

                  <span data-id="element-981">Client Intake Notes (1)</span>
                </label>
                <label className="flex items-center space-x-2 text-sm text-slate-500" data-id="element-982">
                  <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-983" />

                  <span data-id="element-984">Prior Pleadings (0)</span>
                </label>
              </div>
            </div>

            <div className="space-y-2" data-id="element-985">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider" data-id="element-986">
                Tone & Style
              </label>
              <div className="relative" data-id="element-987">
                <select className="w-full h-9 px-3 py-1 text-sm rounded-md border border-input bg-transparent shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring appearance-none" data-id="element-988">
                  <option data-id="element-989">Firm Standard (Firm-wide)</option>
                  <option data-id="element-990">Aggressive / Firm</option>
                  <option data-id="element-991">Collaborative / Settlement-focused</option>
                  <option data-id="element-992">Objective / Neutral</option>
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-993" />
              </div>
            </div>

            <div className="space-y-2" data-id="element-994">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider" data-id="element-995">
                Factual Instructions (Optional)
              </label>
              <textarea className="w-full h-36 p-3 text-sm rounded-md border border-input bg-transparent shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none leading-relaxed" placeholder="E.g., Emphasize the need for future surgery based on Dr. Smith's latest notes. Request $250,000." defaultValue="Emphasize the L4-L5 herniation and the recommendation for future surgical intervention. Demand amount: $250,000." data-id="element-996" />

            </div>
          </CardContent>
          <div className="p-4 border-t bg-slate-50 shrink-0" data-id="element-997">
            <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleGenerate} disabled={isGenerating} data-id="element-998">

              {isGenerating ? <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" data-id="element-999" />
                  Generating Draft...
                </> : <>
                  <Sparkles className="w-4 h-4 mr-2" data-id="element-1000" />
                  Generate First Draft
                </>}
            </Button>
          </div>
        </Card>

        {/* MAIN PANEL: Editor */}
        <Card className="lg:col-span-6 flex flex-col h-full overflow-hidden border-slate-200 shadow-sm relative" data-id="element-1001">
          {!hasGenerated && !isGenerating && <div className="absolute inset-0 z-10 bg-slate-50/80 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center" data-id="element-1002">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4" data-id="element-1003">
                <FileText className="w-8 h-8" data-id="element-1004" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-1005">
                Ready to Draft
              </h3>
              <p className="text-sm text-slate-500 max-w-md" data-id="element-1006">
                Configure your parameters in the left panel and click Generate.
                The AI will synthesize the selected source materials into a
                structured first draft.
              </p>
            </div>}

          <CardHeader className="py-3 border-b bg-slate-50/50 shrink-0 flex flex-row items-center justify-between" data-id="element-1007">
            <div className="flex items-center space-x-2" data-id="element-1008">
              <FileText className="w-4 h-4 text-slate-500" data-id="element-1009" />
              <CardTitle className="text-sm" data-id="element-1010">
                Draft Editor: Demand Letter
              </CardTitle>
              {hasGenerated && <Badge variant="outline" className="ml-2 text-[10px] bg-white" data-id="element-1011">
                  Unsaved Draft
                </Badge>}
            </div>
            <div className="flex space-x-2" data-id="element-1012">
              <Button variant="ghost" size="sm" className="h-8" disabled={!hasGenerated} data-id="element-1013">

                <Download className="w-4 h-4 mr-2" data-id="element-1014" /> Export
              </Button>
              <Button size="sm" className="h-8" disabled={!hasGenerated} data-id="element-1015">
                <Save className="w-4 h-4 mr-2" data-id="element-1016" /> Save to Matter
              </Button>
            </div>
          </CardHeader>

          <div className="flex-1 overflow-y-auto bg-slate-100 p-4 md:p-8" data-id="element-1017">
            {hasGenerated && <div className="bg-white shadow-sm border border-slate-200 rounded-sm min-h-full p-8 md:p-12 font-serif text-sm leading-relaxed text-slate-800 space-y-6 max-w-3xl mx-auto" data-id="element-1018">
                {/* Editor Content - Mocked */}
                <div className="text-right space-y-1 text-slate-600" data-id="element-1019">
                  <p data-id="element-1020">October 26, 2023</p>
                  <p data-id="element-1021">VIA CERTIFIED MAIL</p>
                </div>

                <div className="space-y-1" data-id="element-1022">
                  <p data-id="element-1023">Claims Department</p>
                  <p data-id="element-1024">Lone Star Freight Insurance Co.</p>
                  <p data-id="element-1025">123 Corporate Blvd, Suite 400</p>
                  <p data-id="element-1026">Dallas, TX 75201</p>
                </div>

                <div className="space-y-1 font-bold" data-id="element-1027">
                  <p data-id="element-1028">RE: Our Client: Maria Ramirez</p>
                  <p data-id="element-1029">Your Insured: Lone Star Freight / David Miller</p>
                  <p data-id="element-1030">Date of Loss: October 12, 2023</p>
                  <p data-id="element-1031">
                    Claim No.:{' '}
                    <span className="bg-yellow-200/50 border-b border-yellow-400 text-yellow-900 cursor-help" title="Placeholder: Needs manual input" data-id="element-1032">

                      [INSERT CLAIM NUMBER]
                    </span>
                  </p>
                </div>

                <p data-id="element-1033">Dear Claims Adjuster:</p>

                <p data-id="element-1034">
                  Please be advised that this law firm represents Ms. Maria
                  Ramirez regarding the severe personal injuries she sustained
                  on October 12, 2023, due to the negligence of your insured,
                  Lone Star Freight, and its driver, David Miller.
                  <sup className="text-blue-600 text-xs ml-1 cursor-pointer hover:underline" data-id="element-1035">
                    [1]
                  </sup>
                </p>

                <h3 className="font-bold text-base mt-6 mb-2 uppercase" data-id="element-1036">
                  Liability
                </h3>
                <p data-id="element-1037">
                  On the date of the incident, Ms. Ramirez was traveling
                  lawfully southbound on I-35. Your insured's driver, operating
                  a commercial semi-tractor trailer, failed to maintain a proper
                  lookout and rear-ended Ms. Ramirez's vehicle at a high rate of
                  speed.
                  <sup className="text-blue-600 text-xs ml-1 cursor-pointer hover:underline" data-id="element-1038">
                    [2]
                  </sup>
                  The police report clearly cites your insured for failure to
                  control speed. Liability in this matter is clear and
                  uncontested.
                </p>

                <h3 className="font-bold text-base mt-6 mb-2 uppercase" data-id="element-1039">
                  Injuries and Treatment
                </h3>
                <p data-id="element-1040">
                  The impact of the collision was severe. Ms. Ramirez was
                  transported via EMS to Mercy Hospital Emergency Room
                  complaining of severe lower back pain radiating down her left
                  leg.
                  <sup className="text-blue-600 text-xs ml-1 cursor-pointer hover:underline" data-id="element-1041">
                    [3]
                  </sup>
                </p>

                <p className="relative group" data-id="element-1042">
                  <span className="bg-red-100/50 border-b-2 border-red-400 pb-0.5" data-id="element-1043">
                    She was immediately diagnosed with a severe traumatic injury
                    to her lumbar spine, requiring immediate surgical
                    intervention.
                  </span>
                  <sup className="text-red-600 text-xs ml-1 font-bold cursor-pointer hover:underline" title="Unsupported statement" data-id="element-1044">

                    [!]
                  </sup>
                  {/* Inline comment/warning */}
                  <span className="absolute left-full ml-4 top-0 w-48 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 hidden group-hover:block z-10 shadow-sm" data-id="element-1045">
                    <strong data-id="element-1046">Unsupported:</strong> Medical records indicate MRI
                    was ordered, but no immediate surgery occurred on date of
                    admission.
                  </span>
                </p>

                <p data-id="element-1047">
                  Subsequent MRI imaging ordered by Dr. Smith confirmed an acute
                  disc herniation at L4-L5 with significant nerve root
                  impingement.
                  <sup className="text-blue-600 text-xs ml-1 cursor-pointer hover:underline" data-id="element-1048">
                    [4]
                  </sup>
                  Dr. Smith has recommended future surgical intervention (lumbar
                  fusion) if conservative treatments fail.
                  <sup className="text-blue-600 text-xs ml-1 cursor-pointer hover:underline" data-id="element-1049">
                    [5]
                  </sup>
                </p>

                <h3 className="font-bold text-base mt-6 mb-2 uppercase" data-id="element-1050">
                  Demand
                </h3>
                <p data-id="element-1051">
                  Given the clear liability, the severity of the L4-L5
                  herniation, and the looming prospect of surgical intervention,
                  we hereby demand the sum of <strong data-id="element-1052">$250,000.00</strong> to
                  settle Ms. Ramirez's bodily injury claim.
                </p>

                <p data-id="element-1053">
                  This demand will remain open for thirty (30) days from the
                  date of this letter. We look forward to your prompt response.
                </p>

                <div className="mt-12 space-y-1" data-id="element-1054">
                  <p data-id="element-1055">Sincerely,</p>
                  <p className="mt-8 font-bold" data-id="element-1056">Sarah Jenkins</p>
                  <p data-id="element-1057">Managing Partner</p>
                  <p data-id="element-1058">Festeryga Law</p>
                </div>
              </div>}
          </div>

          {hasGenerated && <div className="p-3 border-t bg-amber-50/50 flex items-center justify-between shrink-0 text-xs" data-id="element-1059">
              <div className="flex items-center text-amber-800" data-id="element-1060">
                <MessageSquareWarning className="w-4 h-4 mr-2 text-amber-600" data-id="element-1061" />
                <span data-id="element-1062">
                  <strong data-id="element-1063">Review required:</strong> 1 unsupported statement, 1
                  unresolved placeholder.
                </span>
              </div>
            </div>}
        </Card>

        {/* RIGHT PANEL: Source Support */}
        <Card className="lg:col-span-3 flex flex-col h-full overflow-hidden border-slate-200 shadow-sm" data-id="element-1064">
          <CardHeader className="py-4 border-b bg-slate-50/50 shrink-0" data-id="element-1065">
            <CardTitle className="text-sm flex items-center" data-id="element-1066">
              <BookOpen className="w-4 h-4 mr-2 text-slate-500" data-id="element-1067" />
              Source Grounding
            </CardTitle>
          </CardHeader>

          <CardContent className="p-0 flex-1 overflow-y-auto" data-id="element-1068">
            {!hasGenerated ? <div className="p-6 text-center text-slate-500 text-sm" data-id="element-1069">
                Generate a draft to see source citations and confidence metrics
                here.
              </div> : <div className="divide-y divide-slate-100" data-id="element-1070">
                {/* Confidence Summary */}
                <div className="p-4 bg-slate-50" data-id="element-1071">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3" data-id="element-1072">
                    Grounding Summary
                  </h4>
                  <div className="space-y-3" data-id="element-1073">
                    <div data-id="element-1074">
                      <div className="flex justify-between text-xs mb-1" data-id="element-1075">
                        <span className="text-slate-600" data-id="element-1076">
                          Overall Factual Support
                        </span>
                        <span className="font-medium text-amber-600" data-id="element-1077">82%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-1.5" data-id="element-1078">
                        <div className="bg-amber-500 h-1.5 rounded-full" style={{
                      width: '82%'
                    }} data-id="element-1079">
                      </div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2 text-xs text-slate-600 bg-white p-2 rounded border border-slate-200" data-id="element-1080">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" data-id="element-1081" />
                      <span data-id="element-1082">5 citations linked to 2 source documents.</span>
                    </div>
                    <div className="flex items-start space-x-2 text-xs text-red-700 bg-red-50 p-2 rounded border border-red-100" data-id="element-1083">
                      <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" data-id="element-1084" />
                      <span data-id="element-1085">1 statement contradicts source records.</span>
                    </div>
                  </div>
                </div>

                {/* Citations List */}
                <div className="p-4" data-id="element-1086">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3" data-id="element-1087">
                    Citations in Draft
                  </h4>
                  <div className="space-y-4" data-id="element-1088">
                    <div className="space-y-1.5" data-id="element-1089">
                      <div className="flex items-center justify-between" data-id="element-1090">
                        <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-200" data-id="element-1091">

                          [1] Intake Notes
                        </Badge>
                        <span className="text-[10px] text-emerald-600 font-medium" data-id="element-1092">
                          High Conf.
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 border-l-2 border-slate-300 pl-2 italic" data-id="element-1093">
                        "Client retained firm on Oct 15 for injuries sustained
                        in Oct 12 collision with Lone Star Freight truck."
                      </p>
                    </div>

                    <div className="space-y-1.5" data-id="element-1094">
                      <div className="flex items-center justify-between" data-id="element-1095">
                        <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-200" data-id="element-1096">

                          [2] Police Report
                        </Badge>
                        <span className="text-[10px] text-emerald-600 font-medium" data-id="element-1097">
                          High Conf.
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 border-l-2 border-slate-300 pl-2 italic" data-id="element-1098">
                        "Unit 2 (Commercial Vehicle) failed to control speed,
                        striking Unit 1 (Ramirez) from behind."
                      </p>
                    </div>

                    <div className="space-y-1.5" data-id="element-1099">
                      <div className="flex items-center justify-between" data-id="element-1100">
                        <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200" data-id="element-1101">

                          [!] Unsupported
                        </Badge>
                        <span className="text-[10px] text-red-600 font-medium" data-id="element-1102">
                          Contradiction
                        </span>
                      </div>
                      <p className="text-xs text-slate-800 font-medium" data-id="element-1103">
                        Draft claims: "...requiring immediate surgical
                        intervention."
                      </p>
                      <p className="text-xs text-slate-600 border-l-2 border-red-300 pl-2 italic bg-red-50/50 p-1" data-id="element-1104">
                        Source (ER Records p.4) states: "Patient stabilized.
                        Discharged with pain medication. Instructed to follow up
                        with orthopedics." No surgery noted on admission date.
                      </p>
                      <Button variant="outline" size="sm" className="w-full text-[10px] h-6 mt-1 text-red-700 border-red-200 hover:bg-red-50" data-id="element-1105">

                        Revise Draft Statement
                      </Button>
                    </div>

                    <div className="space-y-1.5" data-id="element-1106">
                      <div className="flex items-center justify-between" data-id="element-1107">
                        <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-200" data-id="element-1108">

                          [4] Medical Records
                        </Badge>
                        <span className="text-[10px] text-emerald-600 font-medium" data-id="element-1109">
                          High Conf.
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 border-l-2 border-slate-300 pl-2 italic" data-id="element-1110">
                        "MRI Lumbar Spine: Acute disc herniation at L4-L5 with
                        nerve root impingement."
                      </p>
                    </div>
                  </div>
                </div>

                {/* Missing Evidence Warnings */}
                <div className="p-4 bg-amber-50/30" data-id="element-1111">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3" data-id="element-1112">
                    Missing Evidence
                  </h4>
                  <div className="space-y-2" data-id="element-1113">
                    <div className="flex items-start space-x-2 text-xs text-amber-800" data-id="element-1114">
                      <HelpCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-1115" />
                      <span data-id="element-1116">
                        No medical billing records found in corpus to support
                        specific special damages calculation.
                      </span>
                    </div>
                  </div>
                </div>
              </div>}
          </CardContent>
        </Card>
      </div>
    </div>;
}